import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { 
  BookOpen, Calendar, Clock, Target, FileText, 
  ChevronDown, ChevronRight, Printer, CheckCircle2,
  Lightbulb, ClipboardList, BookMarked, FlaskConical
} from 'lucide-react';
import { chemistryWeeklyLessons } from '../../data/chemistryWeeklyLessons';

const ChemistrySchemeOfWork = ({ year = 11 }) => {
  const [selectedTerm, setSelectedTerm] = useState('term1');
  const [expandedWeeks, setExpandedWeeks] = useState({});
  const [expandedDays, setExpandedDays] = useState({});

  // Get data based on year
  const getCurriculumData = () => {
    const yearKey = `year${year}`;
    return chemistryWeeklyLessons[yearKey] || null;
  };

  const yearData = getCurriculumData();
  const termData = yearData?.[selectedTerm];

  const toggleWeek = (weekNum) => {
    setExpandedWeeks(prev => ({
      ...prev,
      [weekNum]: !prev[weekNum]
    }));
  };

  const toggleDay = (weekNum, dayIndex) => {
    const key = `${weekNum}-${dayIndex}`;
    setExpandedDays(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const printLesson = (lesson, weekData) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
      <head>
        <title>Lesson Plan - ${lesson.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          h1 { color: #047857; border-bottom: 2px solid #047857; padding-bottom: 10px; font-size: 18px; }
          h2 { color: #065f46; margin-top: 15px; font-size: 14px; }
          .header { background: #d1fae5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .section { margin: 10px 0; padding: 10px; background: #f8fafc; border-radius: 5px; }
          .safety { background: #fef3c7; border: 2px solid #f59e0b; }
          table { width: 100%; border-collapse: collapse; margin: 10px 0; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #d1fae5; }
          .badge { display: inline-block; background: #d1fae5; color: #047857; padding: 2px 8px; border-radius: 4px; font-size: 11px; margin-right: 5px; }
          ul { margin: 5px 0; padding-left: 20px; }
          li { margin: 3px 0; font-size: 12px; }
          @media print { body { padding: 10px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Year ${year} Chemistry - ${weekData.topic}</h1>
          <p><strong>${lesson.day}:</strong> ${lesson.title}</p>
          <p><span class="badge">${lesson.duration}</span> <span class="badge">${weekData.strand}</span></p>
        </div>
        
        <div class="section">
          <h2>Learning Objectives</h2>
          <ul>
            ${lesson.objectives.map(obj => `<li>${obj}</li>`).join('')}
          </ul>
        </div>
        
        <div class="section">
          <h2>Introduction</h2>
          <p>${lesson.introduction}</p>
        </div>
        
        <h2>Lesson Activities</h2>
        <table>
          <tr><th>Activity</th><th>Duration</th><th>Description</th></tr>
          ${lesson.mainActivities.map(act => `
            <tr>
              <td><strong>${act.activity}</strong></td>
              <td>${act.duration}</td>
              <td>${act.description}</td>
            </tr>
          `).join('')}
        </table>
        
        <div class="section">
          <h2>Materials Needed</h2>
          <p>${lesson.materials.join(', ')}</p>
        </div>
        
        <div class="section">
          <h2>Assessment</h2>
          <p>${lesson.assessment}</p>
        </div>
        
        <div class="section">
          <h2>Homework</h2>
          <p>${lesson.homework}</p>
        </div>
        
        ${lesson.teacherNotes ? `
          <div class="section safety">
            <h2>Teacher Notes</h2>
            <p>${lesson.teacherNotes}</p>
          </div>
        ` : ''}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  if (!termData) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-500 text-center">
            Curriculum data for Year {year} Chemistry {selectedTerm.replace('term', 'Term ')} is being developed. Check back soon!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-t-lg">
        <CardTitle className="flex items-center gap-2 text-xl">
          <FlaskConical className="w-6 h-6" />
          Year {year} Chemistry - Scheme of Work
        </CardTitle>
        <CardDescription className="text-emerald-100">
          {termData.focus} • {termData.weeks} weeks
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4">
        {/* Term Tabs */}
        <Tabs value={selectedTerm} onValueChange={setSelectedTerm} className="mb-4">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="term1">Term 1</TabsTrigger>
            <TabsTrigger value="term2">Term 2</TabsTrigger>
            <TabsTrigger value="term3">Term 3</TabsTrigger>
            <TabsTrigger value="term4">Term 4</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Weekly Scheme */}
        <div className="space-y-4">
          {termData.scheme?.map((week) => (
            <Collapsible 
              key={week.week} 
              open={expandedWeeks[week.week]}
              onOpenChange={() => toggleWeek(week.week)}
            >
              <Card className="border-2 border-emerald-200">
                <CollapsibleTrigger asChild>
                  <CardHeader className="bg-emerald-50 cursor-pointer hover:bg-emerald-100 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {expandedWeeks[week.week] ? 
                          <ChevronDown className="w-5 h-5 text-emerald-600" /> : 
                          <ChevronRight className="w-5 h-5 text-emerald-600" />
                        }
                        <div>
                          <CardTitle className="text-lg text-emerald-800">
                            Week {week.week}: {week.topic}
                          </CardTitle>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <Badge variant="secondary" className="bg-emerald-100">
                              {week.strand}
                            </Badge>
                            <Badge variant="outline" className="text-emerald-600">
                              {week.subStrand}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <Badge className="bg-emerald-600">
                        {week.dailyLessons?.length || 0} Lessons
                      </Badge>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                
                <CollapsibleContent>
                  <CardContent className="pt-4">
                    {/* Learning Outcomes */}
                    <div className="mb-4 p-3 bg-green-50 rounded-lg">
                      <h4 className="font-semibold text-green-800 flex items-center gap-2 mb-2">
                        <Target className="w-4 h-4" /> Learning Outcomes
                      </h4>
                      <ul className="space-y-1">
                        {week.learningOutcomes.map((outcome, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-green-700">
                            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            {outcome}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Activities & Resources */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      <div className="p-3 bg-teal-50 rounded-lg">
                        <h4 className="font-semibold text-teal-800 flex items-center gap-2 mb-2">
                          <ClipboardList className="w-4 h-4" /> Activities
                        </h4>
                        <ul className="space-y-1">
                          {week.activities.map((activity, i) => (
                            <li key={i} className="text-sm text-teal-700">• {activity}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="p-3 bg-cyan-50 rounded-lg">
                        <h4 className="font-semibold text-cyan-800 flex items-center gap-2 mb-2">
                          <BookMarked className="w-4 h-4" /> Resources
                        </h4>
                        <ul className="space-y-1">
                          {week.resources.map((resource, i) => (
                            <li key={i} className="text-sm text-cyan-700">• {resource}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Daily Lessons */}
                    {week.dailyLessons && (
                      <div className="border-t pt-4">
                        <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                          <Calendar className="w-4 h-4" /> Daily Lesson Plans
                        </h4>
                        <div className="space-y-2">
                          {week.dailyLessons.map((lesson, dayIndex) => (
                            <Collapsible
                              key={dayIndex}
                              open={expandedDays[`${week.week}-${dayIndex}`]}
                              onOpenChange={() => toggleDay(week.week, dayIndex)}
                            >
                              <Card className="border border-gray-200">
                                <CollapsibleTrigger asChild>
                                  <div className="p-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {expandedDays[`${week.week}-${dayIndex}`] ? 
                                        <ChevronDown className="w-4 h-4 text-gray-500" /> : 
                                        <ChevronRight className="w-4 h-4 text-gray-500" />
                                      }
                                      <div>
                                        <span className="font-medium text-emerald-600">{lesson.day}:</span>
                                        <span className="ml-2">{lesson.title}</span>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className="text-xs">
                                        <Clock className="w-3 h-3 mr-1" />
                                        {lesson.duration}
                                      </Badge>
                                      <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          printLesson(lesson, week);
                                        }}
                                      >
                                        <Printer className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </CollapsibleTrigger>
                                
                                <CollapsibleContent>
                                  <div className="px-3 pb-3 space-y-3 border-t bg-gray-50">
                                    {/* Objectives */}
                                    <div className="pt-3">
                                      <h5 className="text-sm font-semibold text-gray-700 mb-1">Objectives:</h5>
                                      <ul className="text-sm text-gray-600">
                                        {lesson.objectives.map((obj, i) => (
                                          <li key={i} className="flex items-start gap-1">
                                            <span className="text-emerald-500">•</span> {obj}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>

                                    {/* Introduction */}
                                    <div className="p-2 bg-emerald-50 rounded text-sm">
                                      <span className="font-medium text-emerald-700">Introduction: </span>
                                      <span className="text-emerald-600">{lesson.introduction}</span>
                                    </div>

                                    {/* Main Activities */}
                                    <div>
                                      <h5 className="text-sm font-semibold text-gray-700 mb-2">Activities:</h5>
                                      <div className="space-y-1">
                                        {lesson.mainActivities.map((act, i) => (
                                          <div key={i} className="flex items-start gap-2 text-sm p-2 bg-white rounded border">
                                            <Badge variant="secondary" className="text-xs flex-shrink-0">
                                              {act.duration}
                                            </Badge>
                                            <div>
                                              <span className="font-medium">{act.activity}:</span>
                                              <span className="text-gray-600 ml-1">{act.description}</span>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Materials, Assessment, Homework */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                      <div className="p-2 bg-yellow-50 rounded">
                                        <span className="font-medium text-yellow-800">Materials: </span>
                                        <span className="text-yellow-700">{lesson.materials.join(', ')}</span>
                                      </div>
                                      <div className="p-2 bg-green-50 rounded">
                                        <span className="font-medium text-green-800">Assessment: </span>
                                        <span className="text-green-700">{lesson.assessment}</span>
                                      </div>
                                      <div className="p-2 bg-purple-50 rounded">
                                        <span className="font-medium text-purple-800">Homework: </span>
                                        <span className="text-purple-700">{lesson.homework}</span>
                                      </div>
                                    </div>

                                    {/* Teacher Notes */}
                                    {lesson.teacherNotes && (
                                      <div className="p-2 bg-amber-50 rounded text-sm flex items-start gap-2">
                                        <Lightbulb className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <div>
                                          <span className="font-medium text-amber-800">Teacher Notes: </span>
                                          <span className="text-amber-700">{lesson.teacherNotes}</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </CollapsibleContent>
                              </Card>
                            </Collapsible>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ChemistrySchemeOfWork;
